/**
 * Ad-layer configuration (ads epic — sc-6958/6959).
 *
 * One place to decide which providers are live, per format, plus the network
 * mediator's creds. Ships with the network DARK (enabled: false) so the whole
 * mediator layer is inert — house banners behave exactly as before — until a
 * concrete mediator is chosen and creds are injected. Flipping it on is a
 * config + env change, never a code change at the call sites.
 *
 * Network creds come from Vite build-time env (VITE_AD_*), empty on dev until
 * provisioned. `enabled` gates everything; even with a stray account id, the
 * network stays off unless VITE_AD_NETWORK_ENABLED is explicitly 'true'.
 */

import type { AdFormat } from './provider.js';
import type { NetworkProviderConfig } from './network-provider.js';

interface AdConfig {
  /** Master kill switch for the entire ad layer (house + network). */
  enabled: boolean;
  /**
   * Which cartridges get the in-game banner slot. Only stray-pixel today
   * (its long AFK dwell is the whole reason an in-game banner makes sense).
   */
  bannerCartridges: ReadonlySet<string>;
  /**
   * Formats offered to non-entitled players, across ALL providers (house
   * video + network). A format absent here is off everywhere, regardless of
   * provider fill — the master per-format switch.
   */
  enabledFormats: ReadonlySet<AdFormat>;
  /** Network mediator config (dark until enabled + creds). */
  network: NetworkProviderConfig;
}

/** Read a Vite env flag as a strict boolean ('true' only). */
function envFlag(key: string): boolean {
  return (import.meta.env?.[key] as string | undefined) === 'true';
}

function envStr(key: string): string {
  return (import.meta.env?.[key] as string | undefined) ?? '';
}

export const AD_CONFIG: AdConfig = {
  enabled: true,
  bannerCartridges: new Set(['stray-pixel']),
  // Per-format master switches. Banner + interstitial + rewarded are all
  // served by the HOUSE provider now (bundled video for the latter two), so
  // these are live pre-launch; a network mediator, once enabled, simply
  // competes ahead of house in the same formats.
  enabledFormats: new Set<AdFormat>(['banner', 'interstitial', 'rewarded']),
  network: {
    id: envStr('VITE_AD_NETWORK_ID') || 'network',
    enabled: envFlag('VITE_AD_NETWORK_ENABLED'),
    accountId: envStr('VITE_AD_NETWORK_ACCOUNT_ID'),
    placements: {
      rewarded: envStr('VITE_AD_REWARDED_PLACEMENT') || undefined,
      interstitial: envStr('VITE_AD_INTERSTITIAL_PLACEMENT') || undefined,
      banner: envStr('VITE_AD_BANNER_PLACEMENT') || undefined,
    },
  },
};
